/**
 * A fun animation that animates a "boop" when triggered
 * boop is a small sideways animation
 */
import React from 'react';
import { animated, useSpring } from 'react-spring';

import { ReactFCC } from '@/lib/type-defs/utility';

type BoopType =
  | {
      x?: number;
      y?: number;
      rotation?: number;
      scale?: number;
      timing?: number;
      springConfig?: {
        tension?: number;
        friction?: number;
      };
    }
  | undefined;
export const useBoop = (
  boopConfig: BoopType,
): (AnimatedValue<ForwardedProps<OverwriteKeys<object, React.CSSProperties>>> | (() => void))[] => {
  const { rotation, scale, springConfig, timing, x, y } = {
    x: 0,
    y: 0,
    rotation: 30,
    scale: 1,
    timing: 100,
    ...(boopConfig ?? {}),
    springConfig: {
      tension: 300,
      friction: 10,
      ...(boopConfig?.springConfig ?? {}),
    },
  };
  const [isBooped, setIsBooped] = React.useState(false);
  const style = useSpring({
    display: 'inline-block',
    backfaceVisibility: 'hidden',
    transform: isBooped
      ? `translate(${x}px, ${y}px)
         rotate(${rotation}deg)
         scale(${scale})`
      : `translate(0px, 0px)
         rotate(0deg)
         scale(1)`,
    config: springConfig,
  });
  React.useEffect(() => {
    // We only want to act when we're going from
    // not-booped to booped.
    if (!isBooped) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setIsBooped(false);
    }, timing);
    // Just in case our component happens to
    // unmount while we're booped, cancel
    // the timeout to avoid a memory leak.
    return () => {
      window.clearTimeout(timeoutId);
    };
    // Trigger this effect whenever `isBooped`
    // changes. We also listen for `timing` changes,
    // in case the length of the boop delay is
    // variable.
  }, [isBooped, timing]);
  const trigger = React.useCallback(() => {
    setIsBooped(true);
  }, []);
  return [style, trigger];
};

export const BoopAnimation: ReactFCC<{
  boopConfig?: BoopType;
}> = ({ boopConfig, children }) => {
  const [style, trigger] = useBoop(boopConfig);
  return (
    <animated.span onMouseEnter={trigger} style={style}>
      {children}
    </animated.span>
  );
};
